<script lang="ts">
  import { onDestroy } from 'svelte';
  import './app.css';
  import { AlertCircle, X } from 'lucide-svelte';
  import { getErrorMessage } from './lib/api';
  import { ensureSession, logout as authLogout } from './lib/auth';
  import { uiState, setLoading, clearError, setError } from './lib/state/ui.svelte';
  import { prefetchProjects } from './lib/queries/cache';
  import Login from './lib/components/Login.svelte';
  import ToastContainer from './lib/components/ui/ToastContainer.svelte';
  import CommandPalette from './lib/components/workspace/CommandPalette.svelte';
  import Topbar from './lib/components/workspace/Topbar.svelte';
  import { QueryClientProvider } from '@tanstack/svelte-query';
  import { getQueryClient } from './lib/state/queryClient';
  import {
    router,
    clearEntity,
    goDashboard,
    goGuide,
    openProject,
    openUnit
  } from './lib/router';
  import { fetchProjectById } from './lib/queries/cache';
  import type { Project } from './lib/types/project';
  import type { Unit } from './lib/types/unit';

  let isAuthenticated = $state(false);
  let stopRouting: (() => void) | null = null;
  const queryClient = getQueryClient();

  let resolved = $derived(router.resolved);

  async function checkAuth() {
    const ok = await ensureSession();
    if (ok) {
      isAuthenticated = true;
      await loadInitialData();
    }
  }

  function handleAuth() {
    isAuthenticated = true;
    void loadInitialData();
  }

  function setupRouting() {
    if (!stopRouting) stopRouting = router.start();
  }

  function teardownRouting() {
    if (stopRouting) {
      stopRouting();
      stopRouting = null;
    }
  }

  function handleGlobalKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      if (document.querySelector('[role="dialog"]')) return;
      clearEntity();
      return;
    }
    if ((event.metaKey || event.ctrlKey) && event.key === 'r') {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('open-command-palette'));
    }
  }

  async function logout() {
    await authLogout();
    teardownRouting();
    isAuthenticated = false;
    queryClient.clear();
    goDashboard();
    history.replaceState(null, '', '#/');
  }

  function navigateToView(view: string) {
    if (view === 'guide') goGuide();
    else if (view === 'project') {
      const projectId = router.projectId;
      if (projectId) {
        void fetchProjectById(projectId).then((p) => {
          if (p) openProject(p);
          else goDashboard();
        });
      } else goDashboard();
    } else goDashboard();
  }

  function handleOpenProject(project: Project) {
    openProject(project);
  }

  function handleOpenUnit(unit: Unit) {
    openUnit(unit);
  }

  async function loadInitialData() {
    setLoading(true);
    clearError();

    try {
      await prefetchProjects();
    } catch (error: unknown) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
      if (isAuthenticated) {
        setupRouting();
      }
    }
  }

  checkAuth();

  window.addEventListener('userAuthenticated', handleAuth);
  window.addEventListener('keydown', handleGlobalKeydown);

  onDestroy(() => {
    window.removeEventListener('userAuthenticated', handleAuth);
    window.removeEventListener('keydown', handleGlobalKeydown);
    teardownRouting();
  });
</script>

<svelte:head>
  <title>Toyama RCU Configuration Tool</title>
</svelte:head>

{#if !isAuthenticated}
  <Login />
{:else}
  <QueryClientProvider client={queryClient}>
    <div class="min-h-screen bg-bg-base flex flex-col">
      <Topbar onNavigate={navigateToView} onLogout={logout} />

      {#if resolved.layout === 'workspace'}
        {#if uiState.isLoading}
          <div class="flex flex-1 items-center justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-toyama-teal"></div>
            <span class="ml-3 text-text-muted">Loading...</span>
          </div>
        {:else}
          {#if uiState.errorMessage}
            <div class="mx-3 mt-3 bg-red-50 border border-red-200 rounded p-3">
              <div class="flex">
                <AlertCircle class="w-5 h-5 text-red-400 mt-0.5" />
                <div class="ml-3">
                  <h3 class="text-sm font-medium text-red-800">Error</h3>
                  <p class="text-sm text-red-700 mt-1">{uiState.errorMessage}</p>
                </div>
                <div class="ml-auto pl-3">
                  <button
                    type="button"
                    class="text-red-400 hover:text-red-600"
                    onclick={clearError}
                  >
                    <X class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          {/if}
          <resolved.component />
        {/if}
      {:else}
        <main class="flex-1 min-w-0">
          {#if uiState.isLoading}
            <div class="flex items-center justify-center py-12">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-toyama-teal"></div>
              <span class="ml-3 text-text-muted">Loading...</span>
            </div>
          {:else}
            {#if uiState.errorMessage}
              <div class="m-3 bg-red-50 border border-red-200 rounded p-3">
                <div class="flex">
                  <AlertCircle class="w-5 h-5 text-red-400 mt-0.5" />
                  <div class="ml-3">
                    <h3 class="text-sm font-medium text-red-800">Error</h3>
                    <p class="text-sm text-red-700 mt-1">{uiState.errorMessage}</p>
                  </div>
                  <div class="ml-auto pl-3">
                    <button
                      type="button"
                      class="text-red-400 hover:text-red-600"
                      onclick={clearError}
                    >
                      <X class="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            {/if}

            <resolved.component
              openProject={handleOpenProject}
              openUnit={handleOpenUnit}
            />
          {/if}
        </main>
      {/if}
    </div>

    <ToastContainer />
    <CommandPalette />
  </QueryClientProvider>
{/if}
